import React from "react";
import imageData from "../../data/imageData";

const RARITY_ORDER = ["Common","Uncommon","Rare","Epic","Legendary","Mythic","Ultra"];

function rarityHex(r) {
  return { Common:"#9e9e9e", Uncommon:"#4caf50", Rare:"#2196f3", Epic:"#9c27b0", Legendary:"#ff9800", Mythic:"#ffeb3b", Ultra:"#ff2d78" }[r] || "#fff";
}

function pct(n, total) {
  if (!total) return "0%";
  return ((n / total) * 100).toFixed(1) + "%";
}

function timeAgo(ts) {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60)   return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  return `${Math.floor(diff/86400)}d ago`;
}

export default function Stats({ onClose, inventory, rollCount, totalCoins, coins, prestige, pity, rollHistory = [], streak = 0, battleTrophies = 0, showHistory, onShowHistory, onCloseHistory }) {
  const total = imageData.length;
  const owned = inventory.length;

  const rarityOwned = RARITY_ORDER.map(r => ({
    rarity: r,
    owned:  inventory.filter(i => i.rarity === r).length,
    total:  imageData.filter(i => i.rarity === r).length,
  }));

  const dupeCount   = Math.max(0, rollCount - owned);
  const dupeRate    = pct(dupeCount, rollCount);
  const avgCoins    = rollCount ? (totalCoins / rollCount).toFixed(1) : "—";
  const completion  = pct(owned, total);

  const rarestOwned = inventory.length
    ? inventory.reduce((b, i) => RARITY_ORDER.indexOf(i.rarity) > RARITY_ORDER.indexOf(b.rarity) ? i : b, inventory[0])
    : null;

  const battleWins = parseInt(localStorage.getItem("battleWins") || "0", 10);

  // Roll History modal
  if (showHistory) {
    return (
      <div className="modal-overlay" onClick={onCloseHistory}>
        <div className="modal-box stats-box stats-history-box" onClick={e => e.stopPropagation()}>
          <div className="stats-history-header">
            <button className="stats-history-back" onClick={onCloseHistory}>← Back</button>
            <h2 style={{ margin: 0 }}>🎲 Roll History</h2>
            <div className="stats-history-count">{rollHistory.length} rolls</div>
          </div>

          {rollHistory.length === 0 ? (
            <div className="stats-history-empty">No rolls yet — start rolling!</div>
          ) : (
            <div className="stats-history-list">
              {rollHistory.map((r, i) => {
                const col = rarityHex(r.rarity);
                return (
                  <div key={i} className="stats-history-item" style={{ borderLeft: `3px solid ${col}` }}>
                    <img src={r.image} alt={r.name} className="stats-history-img" style={{ borderColor: `${col}55` }}/>
                    <div className="stats-history-info">
                      <div className="stats-history-name">{r.name}</div>
                      <div className="stats-history-rarity" style={{ color: col }}>✦ {r.rarity}{r.isDupe ? " · ♻️ Dupe" : " · New!"}</div>
                    </div>
                    <div className="stats-history-right">
                      <div className="stats-history-coins">+🪙 {r.earned}</div>
                      <div className="stats-history-time">{timeAgo(r.ts)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box stats-box" onClick={e => e.stopPropagation()}>
        <h2>📊 Stats</h2>

        {prestige > 0 && (
          <div className="stats-prestige-badge">
            ⭐ Prestige {prestige} — +{prestige * 5}% luck · +{prestige * 10}% coins
          </div>
        )}

        <div className="stats-section-label">Rolling</div>
        <div className="stats-grid">
          <div className="stats-card">
            <div className="stats-value">{rollCount.toLocaleString()}</div>
            <div className="stats-label">Total Rolls</div>
          </div>
          <div className="stats-card">
            <div className="stats-value">{owned}<span className="stats-of"> / {total}</span></div>
            <div className="stats-label">Collected</div>
          </div>
          <div className="stats-card">
            <div className="stats-value">{completion}</div>
            <div className="stats-label">Completion</div>
          </div>
          <div className="stats-card">
            <div className="stats-value">{dupeRate}</div>
            <div className="stats-label">Dupe Rate</div>
          </div>
          <div className="stats-card">
            <div className="stats-value">🪙 {avgCoins}</div>
            <div className="stats-label">Avg / Roll</div>
          </div>
          <div className="stats-card">
            <div className="stats-value">🔥 {streak}</div>
            <div className="stats-label">Day Streak</div>
          </div>
        </div>

        {/* Roll History button */}
        <button className="stats-history-btn" onClick={onShowHistory}>
          <span>🎲</span>
          <span>Roll History</span>
          <span className="stats-history-btn-count">{rollHistory.length}</span>
          <span className="stats-history-btn-arrow">→</span>
        </button>

        <div className="stats-section-label">Economy</div>
        <div className="stats-grid stats-grid-2">
          <div className="stats-card">
            <div className="stats-value">🪙 {totalCoins.toLocaleString()}</div>
            <div className="stats-label">Total Earned</div>
          </div>
          <div className="stats-card">
            <div className="stats-value">🪙 {coins.toLocaleString()}</div>
            <div className="stats-label">Balance</div>
          </div>
        </div>

        <div className="stats-section-label">Battle</div>
        <div className="stats-grid stats-grid-2">
          <div className="stats-card">
            <div className="stats-value">⚔️ {battleWins.toLocaleString()}</div>
            <div className="stats-label">Wins</div>
          </div>
          <div className="stats-card">
            <div className="stats-value">🏅 {battleTrophies.toLocaleString()}</div>
            <div className="stats-label">Trophies</div>
          </div>
        </div>

        {rarestOwned && (
          <>
            <div className="stats-section-label">Rarest Owned</div>
            <div className="stats-rarest">
              <img src={rarestOwned.image} alt={rarestOwned.name} className="stats-rarest-img"/>
              <div className="stats-rarest-info">
                <div className="stats-rarest-name">{rarestOwned.name}</div>
                <div className="stats-rarest-rarity" style={{ color: rarityHex(rarestOwned.rarity) }}>
                  ✦ {rarestOwned.rarity}
                </div>
              </div>
            </div>
          </>
        )}

        {pity && (
          <>
            <div className="stats-section-label">Pity</div>
            <div className="stats-pity-row">
              {Object.entries(pity).map(([tier, count]) => {
                const thresholds = { Rare:50, Epic:100, Legendary:200 };
                const max = thresholds[tier];
                const progress = Math.min(count / max, 1);
                return (
                  <div key={tier} className="stats-pity-item">
                    <div className="stats-pity-header">
                      <span style={{ color: rarityHex(tier), fontWeight: 800 }}>{tier}</span>
                      <span className="stats-pity-count">{count} / {max}</span>
                    </div>
                    <div className="stats-pity-bar">
                      <div className="stats-pity-fill" style={{
                        width: `${progress * 100}%`,
                        background: rarityHex(tier),
                        boxShadow: `0 0 8px ${rarityHex(tier)}66`,
                      }}/>
                    </div>
                    {count >= max && <div className="stats-pity-ready">✓ Guaranteed next roll!</div>}
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="stats-section-label" style={{ marginTop: 20 }}>Collection</div>
        {rarityOwned.map(({ rarity, owned: o, total: t }) => (
          <div key={rarity} className="stats-rarity-row">
            <div className="stats-rarity-label" style={{ color: rarityHex(rarity) }}>{rarity}</div>
            <div className="stats-rarity-bar-wrap">
              <div className="stats-rarity-bar">
                <div className="stats-rarity-fill" style={{
                  width: t > 0 ? `${(o/t)*100}%` : "0%",
                  background: rarityHex(rarity),
                  boxShadow: o > 0 ? `0 0 8px ${rarityHex(rarity)}55` : "none",
                }}/>
              </div>
            </div>
            <div className="stats-rarity-count">{o}/{t}</div>
          </div>
        ))}

        <button className="back-button" onClick={onClose} style={{ marginTop: 24, width: "100%" }}>Close</button>
      </div>
    </div>
  );
}